// components/CustomPackageForm.js - Updated Version
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../components/context/AdminContext';

const CustomPackageForm = ({ showForm, setShowForm, setMessage }) => {
    const { apiRequest, currentUser } = useAdmin();
    const [loading, setLoading] = useState(false);
    const [districts, setDistricts] = useState([]);
    const [spots, setSpots] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedSpots, setSelectedSpots] = useState([]);
    const [priceCalculation, setPriceCalculation] = useState(null);
    const [calculationLoading, setCalculationLoading] = useState(false);
    const [spotSearch, setSpotSearch] = useState('');
    const [spotCategory, setSpotCategory] = useState('all');
    const [showSpotDetails, setShowSpotDetails] = useState(null); // For showing spot details modal
    const [availableSpots, setAvailableSpots] = useState([]); // Filtered available spots

    const [formData, setFormData] = useState({
        // Personal Information
        fullName: '',
        email: '',
        phone: '',
        address: '',
        emergencyContact: '',

        // Travel Details
        departureDistrict: '',
        startBranch: '',
        travelDates: {
            startDate: '',
            endDate: ''
        },
        numberOfTravelers: 1,
        numberOfDays: 1,

        // Package Preferences
        accommodationType: 'standard',
        transportation: 'bus',
        mealPlan: 'standard',
        familyMembers: {
            adults: 1,
            children: 0,
            infants: 0
        },

        // Additional Requirements
        specialRequirements: '',
        dietaryRestrictions: '',
        additionalNotes: ''
    });

    // Auto-fill user data
    useEffect(() => {
        if (currentUser && showForm) {
            setFormData(prev => ({
                ...prev,
                fullName: currentUser.name || '',
                email: currentUser.email || '',
                phone: currentUser.phone || '',
                address: currentUser.address || ''
            }));
        }
    }, [currentUser, showForm]);

    // Load initial data
    useEffect(() => {
        if (showForm) {
            loadDistricts();
            loadSpots();
            loadBranches();
        }
    }, [showForm]);

    // Filter spots based on search and category
    useEffect(() => {
        let filtered = spots.filter(spot => spot.isActive !== false);

        if (spotSearch) {
            filtered = filtered.filter(spot =>
                spot.name.toLowerCase().includes(spotSearch.toLowerCase()) ||
                spot.location.toLowerCase().includes(spotSearch.toLowerCase())
            );
        }

        if (spotCategory !== 'all') {
            filtered = filtered.filter(spot => spot.category === spotCategory);
        }

        // Exclude already selected spots
        const selectedSpotIds = selectedSpots.map(s => s.spotId);
        filtered = filtered.filter(spot => !selectedSpotIds.includes(spot._id));

        setAvailableSpots(filtered);
    }, [spots, spotSearch, spotCategory, selectedSpots]);

    // Calculate number of days when dates change
    useEffect(() => {
        if (formData.travelDates.startDate && formData.travelDates.endDate) {
            const start = new Date(formData.travelDates.startDate);
            const end = new Date(formData.travelDates.endDate);
            const timeDiff = end.getTime() - start.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

            if (daysDiff > 0) {
                setFormData(prev => ({
                    ...prev,
                    numberOfDays: Math.max(1, daysDiff)
                }));
            }
        }
    }, [formData.travelDates.startDate, formData.travelDates.endDate]);

    // Calculate price when relevant data changes
    useEffect(() => {
        if (formData.startBranch && selectedSpots.length > 0) {
            calculatePrice();
        } else {
            setPriceCalculation(null);
        }
    }, [
        formData.startBranch,
        selectedSpots,
        formData.accommodationType,
        formData.transportation,
        formData.mealPlan,
        formData.familyMembers,
        formData.numberOfDays,
        formData.numberOfTravelers
    ]);

    // components/CustomPackageForm.js - API calls গুলো ঠিক করুন

    const loadDistricts = async () => {
        try {
            // ✅ GET request - শুধু URL
            const data = await apiRequest('/custom-packages/districts/instant');
            setDistricts(data.data || []);
        } catch (error) {
            console.error('Error loading districts:', error);
        }
    };

    const loadSpots = async () => {
        try {
            // ✅ GET request - শুধু URL
            const data = await apiRequest('/destination-spots');
            setSpots(data.spots || []);
        } catch (error) {
            console.error('Error loading spots:', error);
            setMessage({ type: 'error', text: 'Error loading destination spots' });
        }
    };

    const loadBranches = async () => {
        try {
            // ✅ GET request - শুধু URL
            const data = await apiRequest('/new-branches');
            setBranches(data.branches || []);
        } catch (error) {
            console.log('New branches endpoint failed, trying old endpoint...');

            try {
                // ✅ GET request - শুধু URL
                const oldData = await apiRequest('/branches');
                setBranches(oldData.branches || []);
            } catch (oldError) {
                console.error('Both branch endpoints failed:', oldError);
                setBranches([
                    { _id: '1', name: 'Dhaka' },
                    { _id: '2', name: 'Khulna' }
                ]);
                setMessage({
                    type: 'warning',
                    text: 'Using default branches. Please add branches from admin panel.'
                });
            }
        }
    };

    const calculatePrice = async () => {
        if (!formData.startBranch || selectedSpots.length === 0) return;

        setCalculationLoading(true);
        try {
            // ✅ ঠিক করুন: apiRequest(url, method, body) ফরম্যাটে
            const calculation = await apiRequest(
                '/custom-packages/calculate-price',
                'POST',
                {
                    startBranch: formData.startBranch,
                    selectedSpots,
                    accommodationType: formData.accommodationType,
                    transportation: formData.transportation,
                    numberOfTravelers: parseInt(formData.numberOfTravelers),
                    mealPlan: formData.mealPlan,
                    familyMembers: formData.familyMembers,
                    numberOfDays: parseInt(formData.numberOfDays)
                }
            );

            if (calculation.success) {
                setPriceCalculation(calculation);
            }
        } catch (error) {
            console.error('Price calculation error:', error);
            setMessage({ type: 'error', text: 'Error calculating price: ' + error.message });
        } finally {
            setCalculationLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!currentUser) {
            setMessage({ type: 'error', text: 'Please login to submit a custom package request' });
            return;
        }

        if (selectedSpots.length === 0) {
            setMessage({ type: 'error', text: 'Please select at least one destination spot' });
            return;
        }

        if (!formData.travelDates.startDate || !formData.travelDates.endDate) {
            setMessage({ type: 'error', text: 'Please select both start and end dates' });
            return;
        }

        const startDate = new Date(formData.travelDates.startDate);
        const endDate = new Date(formData.travelDates.endDate);

        if (endDate <= startDate) {
            setMessage({ type: 'error', text: 'End date must be after start date' });
            return;
        }

        if (!priceCalculation) {
            setMessage({ type: 'error', text: 'Please wait for price calculation to complete' });
            return;
        }

        setLoading(true);

        try {
            const submissionData = {
                ...formData,
                selectedSpots,
                pricingBreakdown: priceCalculation.breakdown,
                estimatedPrice: priceCalculation.estimatedPrice
            };

            // ✅ ঠিক করুন: apiRequest(url, method, body) ফরম্যাটে
            const data = await apiRequest(
                '/custom-packages/request',
                'POST',
                submissionData
            );

            if (data.success) {
                setMessage({
                    type: 'success',
                    text: '🎉 Your custom package request has been submitted successfully! We will contact you within 24 hours.'
                });
                setShowForm(false);
                resetForm();
            } else {
                throw new Error(data.error || 'Unknown error occurred');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setMessage({ type: 'error', text: 'Error submitting request: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleSpotSelection = (spotId) => {
        if (!spotId) return;

        const spot = spots.find(s => s._id === spotId);
        if (!spot) return;

        // Calculate fare based on selected branch
        const branchKey = formData.startBranch.toLowerCase();
        const fare = spot.fares && spot.fares[branchKey]
            ? spot.fares[branchKey]
            : spot.fares?.dhaka || 0;

        setSelectedSpots(prev => [...prev, {
            spotId: spot._id,
            spotName: spot.name,
            category: spot.category,
            location: spot.location,
            fare: fare
        }]);

        // Clear search after selection
        setSpotSearch('');
    };

    const handleRemoveSpot = (spotId) => {
        setSelectedSpots(prev => prev.filter(s => s.spotId !== spotId));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('travelDates.')) {
            const dateField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                travelDates: {
                    ...prev.travelDates,
                    [dateField]: value
                }
            }));
        } else if (name.startsWith('familyMembers.')) {
            const memberField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                familyMembers: {
                    ...prev.familyMembers,
                    [memberField]: parseInt(value) || 0
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const showSpotDetailModal = (spotId) => {
        const spot = spots.find(s => s._id === spotId);
        if (spot) {
            setShowSpotDetails(spot);
        }
    };

    const resetForm = () => {
        setFormData({
            fullName: currentUser?.name || '',
            email: currentUser?.email || '',
            phone: currentUser?.phone || '',
            address: currentUser?.address || '',
            emergencyContact: '',
            departureDistrict: '',
            startBranch: '',
            travelDates: { startDate: '', endDate: '' },
            numberOfTravelers: 1,
            numberOfDays: 1,
            accommodationType: 'standard',
            transportation: 'bus',
            mealPlan: 'standard',
            familyMembers: { adults: 1, children: 0, infants: 0 },
            specialRequirements: '',
            dietaryRestrictions: '',
            additionalNotes: ''
        });
        setSelectedSpots([]);
        setPriceCalculation(null);
        setSpotSearch('');
        setSpotCategory('all');
        setShowSpotDetails(null);
    };

    const formatCurrency = (amount) => {
        return `৳${amount?.toLocaleString() || '0'}`;
    };

    const categories = [
        { value: 'all', label: 'All Categories' },
        { value: 'beach', label: 'Beach' },
        { value: 'mountain', label: 'Mountain' },
        { value: 'historical', label: 'Historical' },
        { value: 'adventure', label: 'Adventure' },
        { value: 'cultural', label: 'Cultural' },
        { value: 'religious', label: 'Religious' }
    ];

    const mealPlans = [
        { value: 'basic', label: 'Basic Meal Plan', description: 'Simple local cuisine' },
        { value: 'standard', label: 'Standard Meal Plan', description: 'Balanced meals with variety' },
        { value: 'premium', label: 'Premium Meal Plan', description: 'Gourmet dining experience' },
        { value: 'custom', label: 'Custom Meal Plan', description: 'Tailored to your preferences' }
    ];

    if (!showForm) return null;

    return (
        <>
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content" style={{ maxHeight: '95vh' }}>
                        <div className="modal-header bg-primary text-white sticky-top">
                            <h5 className="modal-title">
                                <i className="fas fa-magic me-2"></i>
                                Customize Your Dream Package
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={() => setShowForm(false)}
                                disabled={loading}
                            ></button>
                        </div>

                        <div className="modal-body" style={{ overflowY: 'auto' }}>
                            {/* User Status */}
                            {!currentUser ? (
                                <div className="alert alert-warning">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Please login to submit a custom package request.
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    <i className="fas fa-user-check me-2"></i>
                                    Logged in as: <strong>{currentUser.name}</strong> ({currentUser.email})
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                {/* Personal Information Section */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="fas fa-user me-2"></i>
                                            Personal Information
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Full Name *</label>
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    required
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Email *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    required
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Phone Number *</label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    required
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Emergency Contact</label>
                                                <input
                                                    type="text"
                                                    name="emergencyContact"
                                                    value={formData.emergencyContact}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">Address</label>
                                                <textarea
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    rows="2"
                                                    className="form-control"
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Travel Details Section */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="fas fa-map-marked-alt me-2"></i>
                                            Travel Details
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Departure District *</label>
                                                <select
                                                    name="departureDistrict"
                                                    value={formData.departureDistrict}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    <option value="">Select Your District</option>
                                                    {districts.map((district) => (
                                                        <option key={district._id} value={district.district}>
                                                            {district.district}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Start Branch *</label>
                                                <select
                                                    name="startBranch"
                                                    value={formData.startBranch}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    <option value="">Select Branch</option>
                                                    {branches.map(branch => {
                                                        const branchCode = branch.name ? branch.name.toLowerCase().trim() : '';
                                                        return (
                                                            <option key={branch._id} value={branchCode}>
                                                                {branch.name} Branch
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Start Date *</label>
                                                <input
                                                    type="date"
                                                    name="travelDates.startDate"
                                                    value={formData.travelDates.startDate}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    required
                                                    disabled={loading || !currentUser}
                                                    min={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">End Date *</label>
                                                <input
                                                    type="date"
                                                    name="travelDates.endDate"
                                                    value={formData.travelDates.endDate}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    required
                                                    disabled={loading || !currentUser}
                                                    min={formData.travelDates.startDate || new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Days</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={formData.numberOfDays}
                                                    disabled
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Travelers *</label>
                                                <select
                                                    name="numberOfTravelers"
                                                    value={formData.numberOfTravelers}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                                        <option key={num} value={num}>
                                                            {num} {num === 1 ? 'Person' : 'Persons'}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Destination Spots Selection - NEW DESIGN */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="fas fa-map-marker-alt me-2"></i>
                                            Select Destination Spots *
                                        </h6>
                                        <small className="text-muted">Choose the spots you want to visit</small>
                                    </div>
                                    <div className="card-body">
                                        {!formData.startBranch ? (
                                            <div className="alert alert-warning text-center">
                                                <i className="fas fa-info-circle me-2"></i>
                                                Please select a start branch first to view available spots.
                                            </div>
                                        ) : (
                                            <>
                                                {/* Selected Spots Display */}
                                                <div className="mb-4">
                                                    <label className="form-label">Selected Spots:</label>
                                                    {selectedSpots.length === 0 ? (
                                                        <div className="alert alert-light text-center">
                                                            <i className="fas fa-map-marker-alt text-muted me-2"></i>
                                                            No spots selected yet. Select spots from the dropdown below.
                                                        </div>
                                                    ) : (
                                                        <div className="selected-spots-container">
                                                            {selectedSpots.map((spot, index) => (
                                                                <div key={spot.spotId} className="selected-spot-item mb-2">
                                                                    <div className="card">
                                                                        <div className="card-body py-2">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <div>
                                                                                    <span className="badge bg-primary me-2">
                                                                                        {index + 1}
                                                                                    </span>
                                                                                    <strong>{spot.spotName}</strong>
                                                                                    <small className="text-muted ms-2">
                                                                                        ({spot.category}) - {spot.location}
                                                                                    </small>
                                                                                </div>
                                                                                <div className="d-flex align-items-center">
                                                                                    <span className="text-success fw-bold me-3">
                                                                                        {formatCurrency(spot.fare)}
                                                                                    </span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        onClick={() => handleRemoveSpot(spot.spotId)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        <i className="fas fa-times"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Spot Selection Input */}
                                                <div className="row mb-4">
                                                    <div className="col-md-8">
                                                        <label className="form-label">Add More Spots</label>
                                                        <div className="input-group">
                                                            <select
                                                                className="form-select"
                                                                value={spotSearch}
                                                                onChange={(e) => handleSpotSelection(e.target.value)}
                                                                disabled={loading || availableSpots.length === 0}
                                                            >
                                                                <option value="">Select a spot to add...</option>
                                                                {availableSpots.map(spot => (
                                                                    <option key={spot._id} value={spot._id}>
                                                                        {spot.name} - {spot.category} ({spot.location}) -
                                                                        {formatCurrency(spot.fares[formData.startBranch] || 0)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={() => {
                                                                    if (spotSearch) {
                                                                        handleSpotSelection(spotSearch);
                                                                    }
                                                                }}
                                                                disabled={!spotSearch || loading}
                                                            >
                                                                <i className="fas fa-plus"></i> Add
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label">Filter by Category</label>
                                                        <select
                                                            className="form-select"
                                                            value={spotCategory}
                                                            onChange={(e) => setSpotCategory(e.target.value)}
                                                            disabled={loading}
                                                        >
                                                            {categories.map(cat => (
                                                                <option key={cat.value} value={cat.value}>
                                                                    {cat.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Available Spots Count */}
                                                <div className="alert alert-info">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    Available spots: <strong>{availableSpots.length}</strong> |
                                                    Selected spots: <strong>{selectedSpots.length}</strong>
                                                    {availableSpots.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm ms-2"
                                                            onClick={() => setShowSpotDetails('all')}
                                                        >
                                                            View all available spots details
                                                        </button>
                                                    )}
                                                </div>

                                                {availableSpots.length === 0 && (
                                                    <div className="text-center py-4">
                                                        <i className="fas fa-map-marker-alt fa-2x text-muted mb-3"></i>
                                                        <p className="text-muted">
                                                            {selectedSpots.length > 0
                                                                ? "All spots have been selected or no more spots available for this category."
                                                                : "No spots found matching your criteria or no spots available from this branch."
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Package Preferences */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="fas fa-cogs me-2"></i>
                                            Package Preferences
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            {/* Family Members */}
                                            <div className="col-12">
                                                <h6 className="border-bottom pb-2">Family Members Breakdown</h6>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Adults *</label>
                                                <input
                                                    type="number"
                                                    name="familyMembers.adults"
                                                    value={formData.familyMembers.adults}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    min="1"
                                                    max="20"
                                                    required
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Children (2-12 years)</label>
                                                <input
                                                    type="number"
                                                    name="familyMembers.children"
                                                    value={formData.familyMembers.children}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    min="0"
                                                    max="20"
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Infants (Below 2 years)</label>
                                                <input
                                                    type="number"
                                                    name="familyMembers.infants"
                                                    value={formData.familyMembers.infants}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    min="0"
                                                    max="10"
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>

                                            {/* Accommodation & Transport */}
                                            <div className="col-md-6">
                                                <label className="form-label">Accommodation Type *</label>
                                                <select
                                                    name="accommodationType"
                                                    value={formData.accommodationType}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    <option value="standard">Standard Hotel</option>
                                                    <option value="deluxe">Deluxe Hotel</option>
                                                    <option value="luxury">Luxury Hotel</option>
                                                    <option value="resort">Beach Resort</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Transportation *</label>
                                                <select
                                                    name="transportation"
                                                    value={formData.transportation}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    <option value="bus">AC Bus</option>
                                                    <option value="train">Train</option>
                                                    <option value="flight">Flight</option>
                                                    <option value="private_car">Private Car</option>
                                                </select>
                                            </div>

                                            {/* Meal Plan */}
                                            <div className="col-12">
                                                <label className="form-label">Meal Plan *</label>
                                                <select
                                                    name="mealPlan"
                                                    value={formData.mealPlan}
                                                    onChange={handleInputChange}
                                                    className="form-select"
                                                    required
                                                    disabled={loading || !currentUser}
                                                >
                                                    {mealPlans.map(plan => (
                                                        <option key={plan.value} value={plan.value}>
                                                            {plan.label} - {plan.description}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Requirements */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <i className="fas fa-clipboard-list me-2"></i>
                                            Additional Requirements
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label">Special Requirements</label>
                                                <textarea
                                                    name="specialRequirements"
                                                    value={formData.specialRequirements}
                                                    onChange={handleInputChange}
                                                    rows="3"
                                                    className="form-control"
                                                    placeholder="Any special requirements, preferences, or specific places you want to visit..."
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Dietary Restrictions</label>
                                                <input
                                                    type="text"
                                                    name="dietaryRestrictions"
                                                    value={formData.dietaryRestrictions}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    placeholder="Any food allergies or dietary preferences..."
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Additional Notes</label>
                                                <input
                                                    type="text"
                                                    name="additionalNotes"
                                                    value={formData.additionalNotes}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    placeholder="Any other important information..."
                                                    disabled={loading || !currentUser}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Price Calculation Display */}
                                {priceCalculation && (
                                    <div className="card mb-4 border-success">
                                        <div className="card-header bg-success text-white">
                                            <h6 className="mb-0">
                                                <i className="fas fa-calculator me-2"></i>
                                                Price Calculation
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <strong>Selected Spots ({selectedSpots.length}):</strong>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            {formatCurrency(priceCalculation.breakdown.spotsTotal)}
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <strong>Accommodation ({formData.numberOfDays} days):</strong>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            {formatCurrency(priceCalculation.breakdown.accommodationCost)}
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <strong>Transport ({formData.transportation}):</strong>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            {formatCurrency(priceCalculation.breakdown.transportCost)}
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <strong>Meals ({formData.mealPlan}):</strong>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            {formatCurrency(priceCalculation.breakdown.mealCost)}
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2 border-top pt-2">
                                                        <div className="col-6">
                                                            <strong>Subtotal:</strong>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <strong>{formatCurrency(priceCalculation.breakdown.totalBeforeMultiplier)}</strong>
                                                        </div>
                                                    </div>
                                                    <div className="row mb-2">
                                                        <div className="col-6">
                                                            <small>× Branch Multiplier:</small>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <small>{priceCalculation.breakdown.branchMultiplier}x</small>
                                                        </div>
                                                    </div>
                                                    <div className="row border-top pt-2">
                                                        <div className="col-6">
                                                            <h5 className="text-success mb-0">Total Estimated Price:</h5>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <h4 className="text-success mb-0">
                                                                {formatCurrency(priceCalculation.estimatedPrice)}
                                                            </h4>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4 border-start">
                                                    <div className="text-center">
                                                        <div className="bg-light rounded p-3">
                                                            <h6 className="text-primary text-uppercase">
                                                                {formData.startBranch} BRANCH
                                                            </h6>
                                                            <div className="small text-muted mb-2">
                                                                {priceCalculation.branchPricing.description}
                                                            </div>
                                                            <div className="badge bg-primary mb-2">
                                                                Fixed Base: {formatCurrency(priceCalculation.branchPricing.fixedBasePrice)}
                                                            </div>
                                                            <div className="badge bg-success">
                                                                Multiplier: {priceCalculation.breakdown.branchMultiplier}x
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {calculationLoading && (
                                    <div className="alert alert-info text-center">
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Calculating price...
                                    </div>
                                )}

                                {/* Form Actions */}
                                <div className="modal-footer sticky-bottom bg-white pt-3 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowForm(false)}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading || !currentUser || !priceCalculation || selectedSpots.length === 0}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-paper-plane me-2"></i>
                                                Submit Request - {priceCalculation ? formatCurrency(priceCalculation.estimatedPrice) : 'Calculate Price'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Spot Details Modal */}
            {showSpotDetails && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-info-circle me-2"></i>
                                    Spot Details
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowSpotDetails(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {showSpotDetails === 'all' ? (
                                    <div>
                                        <h6>All Available Spots ({availableSpots.length})</h6>
                                        <div className="row mt-3">
                                            {availableSpots.map(spot => (
                                                <div key={spot._id} className="col-md-6 mb-3">
                                                    <div className="card h-100">
                                                        <div className="card-body">
                                                            <h6 className="card-title">{spot.name}</h6>
                                                            <div className="mb-2">
                                                                <span className="badge bg-primary me-1">
                                                                    {spot.category}
                                                                </span>
                                                                <span className="badge bg-secondary">
                                                                    {spot.recommendedSeason}
                                                                </span>
                                                            </div>
                                                            <p className="card-text small">{spot.description}</p>
                                                            <div className="mb-2">
                                                                <small className="text-muted">
                                                                    <i className="fas fa-location-dot me-1"></i>
                                                                    {spot.location}
                                                                </small>
                                                            </div>
                                                            <div className="text-success fw-bold">
                                                                Fare: {formatCurrency(spot.fares[formData.startBranch] || 0)}
                                                            </div>
                                                            {spot.features.length > 0 && (
                                                                <div className="mt-2">
                                                                    <small className="text-muted">Features:</small>
                                                                    <div>
                                                                        {spot.features.slice(0, 3).map((feature, idx) => (
                                                                            <span key={idx} className="badge bg-light text-dark me-1 mb-1 small">
                                                                                {feature}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h5>{showSpotDetails.name}</h5>
                                        <div className="mb-3">
                                            <span className="badge bg-primary me-2">{showSpotDetails.category}</span>
                                            <span className="badge bg-secondary">{showSpotDetails.recommendedSeason}</span>
                                        </div>
                                        <p>{showSpotDetails.description}</p>
                                        <div className="mb-3">
                                            <strong>Location:</strong> {showSpotDetails.location}
                                        </div>
                                        <div className="mb-3">
                                            <strong>Fares:</strong>
                                            <div className="row mt-2">
                                                <div className="col-md-6">
                                                    <strong>Dhaka:</strong> {formatCurrency(showSpotDetails.fares.dhaka)}
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Khulna:</strong> {formatCurrency(showSpotDetails.fares.khulna)}
                                                </div>
                                            </div>
                                            {formData.startBranch && (
                                                <div className="mt-2 text-success">
                                                    <strong>From {formData.startBranch}:</strong>
                                                    {formatCurrency(showSpotDetails.fares[formData.startBranch] || showSpotDetails.fares.dhaka)}
                                                </div>
                                            )}
                                        </div>
                                        {showSpotDetails.features.length > 0 && (
                                            <div className="mb-3">
                                                <strong>Features:</strong>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {showSpotDetails.features.map((feature, idx) => (
                                                        <span key={idx} className="badge bg-primary">
                                                            {feature}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowSpotDetails(null)}
                                >
                                    Close
                                </button>
                                {showSpotDetails !== 'all' && !selectedSpots.some(s => s.spotId === showSpotDetails._id) && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            handleSpotSelection(showSpotDetails._id);
                                            setShowSpotDetails(null);
                                        }}
                                    >
                                        <i className="fas fa-plus me-1"></i>
                                        Add to Trip
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomPackageForm;